import { useState } from "react";
import { VStack } from "@design-system/VStack/VStack";
import { useAnatomyMode } from "./componentAnatomyMode";
import type { MenuRootProps } from "@design-system/Menu/Menu.lib";
import { MenuPlaygroundExamples } from "./MenuPlaygroundExamples";
import {
    MenuPlaygroundStage,
    type MenuPlaygroundRowKind,
} from "./MenuPlaygroundStage";
import {
    MenuPlaygroundTriggerControls,
    defaultTriggerOptions,
    type MenuPlaygroundTriggerOptions,
} from "./MenuPlaygroundTrigger";
import { BooleanControl, SelectControl } from "./playgroundControls";
import styles from "./MenuPlayground.module.scss";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

const rowKindOptions: ReadonlyArray<{
    value: MenuPlaygroundRowKind;
    label: string;
}> = [
    { value: "item", label: "Item" },
    { value: "submenu", label: "Submenu trigger" },
    { value: "toggle", label: "Toggle item" },
    { value: "radio", label: "Radio item (selected)" },
];

/** The anatomy variant of the stage's first row. */
const variantIdFor = (
    rowKind: MenuPlaygroundRowKind,
    isDestructive: boolean,
) => {
    if (rowKind !== "item") {
        return rowKind;
    }
    return isDestructive ? "destructive" : "default";
};

type StageOpenChangeDetails = Parameters<
    NonNullable<MenuRootProps["onOpenChange"]>
>[1];

const outsideCloseReasons: readonly string[] = ["outside-press", "focus-out"];

/**
 * A close caused by a click or focus move into the aside's controls, including
 * the popups of its selects (Picker popups portal outside the aside).
 */
const isCloseFromControls = ({ reason, event }: StageOpenChangeDetails) => {
    const target =
        event instanceof FocusEvent ? event.relatedTarget : event.target;
    return (
        outsideCloseReasons.includes(reason) &&
        target instanceof Element &&
        target.closest(
            "[data-menu-playground-controls], [data-picker-popup]",
        ) !== null
    );
};

/**
 * Sandbox for the action menu: one live menu whose trigger and first row
 * follow the aside's controls, the token x-ray of that row, and the static
 * examples and state matrix below the stage. The stage menu starts open,
 * stays open while the aside is used and reopens on every control change,
 * so each control shows its effect at once; elsewhere it closes as usual.
 */
export const MenuPlayground = () => {
    const [rowKind, setRowKind] = useState<MenuPlaygroundRowKind>("item");
    const [isDestructive, setIsDestructive] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [lastAction, setLastAction] = useState<string | null>(null);
    const [trigger, setTrigger] = useState<MenuPlaygroundTriggerOptions>(
        defaultTriggerOptions,
    );
    const [isStageOpen, setIsStageOpen] = useState(true);
    const handleStageOpenChange: NonNullable<MenuRootProps["onOpenChange"]> = (
        isOpen,
        eventDetails,
    ) => {
        if (!isOpen && isCloseFromControls(eventDetails)) {
            return;
        }
        setIsStageOpen(isOpen);
    };
    const withStageOpen =
        <T,>(setter: (value: T) => void) =>
        (value: T) => {
            setter(value);
            setIsStageOpen(true);
        };
    // Destructive only styles plain item rows; other kinds ignore it.
    const isDestructiveRow = rowKind === "item" && isDestructive;
    const variantId = variantIdFor(rowKind, isDestructiveRow);
    const anatomy = useAnatomyMode({
        component: "menu",
        variantId,
        isDisabled,
    });
    const summary = isDisabled ? `${variantId} · disabled` : variantId;

    return (
        <SandboxLayout
            controls={
                <div
                    className={styles.controlsScope}
                    data-menu-playground-controls
                >
                    {anatomy.control}
                    <MenuPlaygroundTriggerControls
                        options={trigger}
                        onOptionsChange={withStageOpen(setTrigger)}
                    />
                    <section>
                        <h2>First row</h2>
                        <VStack gap={10}>
                            <SelectControl
                                label="Kind"
                                options={rowKindOptions}
                                value={rowKind}
                                onChange={withStageOpen(setRowKind)}
                            />
                            <BooleanControl
                                label="Destructive (items only)"
                                checked={isDestructiveRow}
                                disabled={rowKind !== "item"}
                                onChange={withStageOpen(setIsDestructive)}
                            />
                            <BooleanControl
                                label="Disabled (with reason)"
                                checked={isDisabled}
                                onChange={withStageOpen(setIsDisabled)}
                            />
                        </VStack>
                    </section>
                </div>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={<MenuPlaygroundExamples />}
                output={JSON.stringify(
                    {
                        trigger,
                        rowKind,
                        isDestructive: isDestructiveRow,
                        isDisabled,
                        forcedState: anatomy.forcedState ?? null,
                        lastAction,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <MenuPlaygroundStage
                        rowKind={rowKind}
                        isDestructive={isDestructiveRow}
                        isDisabled={isDisabled}
                        forcedState={anatomy.forcedState}
                        onAction={setLastAction}
                        trigger={trigger}
                        isOpen={isStageOpen || anatomy.forcedState != null}
                        onOpenChange={handleStageOpenChange}
                    />
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
